import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Flex from '../../../components/Flex';
import { Drawer, Dropdown, useTheme } from '@prio365/prio365-react-library';
import { PrioTheme } from '../../../theme/types';
import ActionBar from '../../../components/ActionBar';
import JiraConfigurationForm from './JiraConfigurationForm';
import SvgIcon from '../../../components/SvgIcon';
import { getSvgIconPathByType } from '../../../util/icon';
import JiraConfigurationCreate from './JiraConfigurationCreate';
import { makePrioStyles } from '../../../theme/utils';
import classNames from 'classnames';
import { Setting, SettingKey } from '../../../models/Settings';
import { apiFetchSettings } from '../api';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    height: '100%',
    overflow: 'hidden',
  },
  item: {
    color: theme.colors.application.typography.default,
    fontWeight: theme.font.fontSize.regular,
  },
  subItem: {
    color: theme.colors.application.typography.muted,
    fontWeight: theme.font.fontSize.extraSmall,
    float: 'left',
  },
  listItem: {
    padding: `${theme.spacing.regular * 0.5}px ${theme.spacing.regular}px`,
    cursor: 'pointer',
    width: '100%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    '& h2': {
      width: '100%',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      marginBottom: `${theme.spacing.regular * 0.5}px`,
    },
    '&:hover': {
      backgroundColor: theme.colors.application.background.hover,
    },
  },
  icon: {
    minWidth: theme.spacing.regular * 1.5,
    fontSize: theme.font.fontSize.extraSmall,
  },
  overlay: {
    '& > ul': {
      paddingInlineStart: '0px',
      marginBlock: '0.5rem',
    },
  },
}));

interface ConfigAddInsProps {
  className?: string;
}

export const ConfigAddIns: React.FC<ConfigAddInsProps> = (props) => {
  //#region ------------------------------ Defaults
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  const classes = useStyles();
  const extensionTypes = useMemo(() => ['jira'], []);

  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [selectedExtensionType, setSelectedExtensionType] =
    useState<string>('');
  const [jiraSettings, setJiraSettings] = useState<Setting>(null);
  const [hasValidJiraSetting, setHasValidJiraSetting] =
    useState<boolean>(false);
  const [openNew, setOpenNew] = useState<boolean>(false);
  const [openExisting, setOpenExisting] = useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleSelectExtensionType = (selectedOption) => {
    if (selectedOption) {
      const index = parseInt(selectedOption.value);
      setSelectedExtensionType(extensionTypes[index]);
      setOpenNew(true);
    }
  };

  const projectExtensionConfigurationChanged = async () => {
    const { data: jiraGlobalConnectionOptions } = await apiFetchSettings([
      {
        keys: ['ProjectExtension_Jira_GlobalConnectionOptions'],
        eSettingScope: 'global',
      } as SettingKey,
    ]);
    setJiraSettings(jiraGlobalConnectionOptions);
    setOpenNew(false);
  };

  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    setHasValidJiraSetting(
      !!jiraSettings?.[0] &&
        jiraSettings?.[0]?.settingId !== '00000000-0000-0000-0000-000000000000'
    );
  }, [jiraSettings]);

  useEffect(() => {
    const fetchProjectExtensionUserConfiguration = async () => {
      const { data: jiraGlobalConnectionOptions } = await apiFetchSettings([
        {
          keys: ['ProjectExtension_Jira_GlobalConnectionOptions'],
          eSettingScope: 'global',
        } as SettingKey,
      ]);
      setJiraSettings(jiraGlobalConnectionOptions);
    };
    fetchProjectExtensionUserConfiguration();
  }, []);
  //#endregion

  const options = extensionTypes?.map((extType, index) => ({
    label: extType.charAt(0).toUpperCase() + extType.slice(1),
    value: index.toString(),
    disabled: hasValidJiraSetting,
  }));

  return (
    <>
      <Flex.Column flex={1} className={classes.root}>
        <Flex.Row childrenGap={theme.spacing.regular} marginBottom={16}>
          <ActionBar>
            <Dropdown
              options={options}
              onSelect={handleSelectExtensionType}
              overlayClassName={classNames(classes.overlay, 'prio-dropdown')}
              buttonProps={{
                type: 'primary',
                suffixIconProp: ['fal', 'chevron-down'],
                disabled: options?.every((option) => option.disabled),
              }}
              defaultLabel={t('settings:addInsConfig.newConfig')}
              placement="bottomRight"
            ></Dropdown>
          </ActionBar>
        </Flex.Row>
        <Flex.Row className={classes.root}>
          <div style={{ background: 'white', width: '400px' }}>
            {!hasValidJiraSetting ? (
              <div
                style={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontStyle: 'italic',
                }}
              >
                {t('settings:addInsConfig.emptyText')}
              </div>
            ) : (
              <div
                className={classes.listItem}
                onClick={() => {
                  setOpenExisting(true);
                }}
              >
                <Flex.Row
                  className={classes.item}
                  childrenGap={theme.spacing.extraSmall}
                  alignItems="center"
                >
                  <SvgIcon
                    width={40}
                    height={40}
                    path={getSvgIconPathByType('jira')}
                  ></SvgIcon>
                  <div style={{ fontSize: '16px', fontWeight: '500' }}>
                    Jira
                  </div>
                </Flex.Row>
              </div>
            )}
          </div>
        </Flex.Row>
      </Flex.Column>
      <Drawer
        title={t('settings:jiraConfig.newJiraConfig')}
        visible={openNew}
        closable={true}
        onClose={() => {
          setOpenNew(false);
        }}
        footer={<></>}
      >
        {selectedExtensionType === 'jira' && (
          <JiraConfigurationCreate
            projectExtensionConfigurationChanged={
              projectExtensionConfigurationChanged
            }
            closeDrawer={() => {
              setOpenNew(false);
            }}
          />
        )}
      </Drawer>
      <Drawer
        title={t('projects:addIn.jiraConfig.existingJiraConfig')}
        visible={openExisting}
        closable={true}
        onClose={() => {
          setOpenExisting(false);
        }}
      >
        <JiraConfigurationForm
          projectExtensionConfigurationChanged={
            projectExtensionConfigurationChanged
          }
          jiraSetting={jiraSettings}
          onClose={() => {
            setOpenExisting(false);
          }}
        />
      </Drawer>
    </>
  );
};

export default ConfigAddIns;
